import { useCallback, useEffect, useMemo, useState } from "react";
import { adminApi } from "../../lib/api";
import AdminShell from "../../components/AdminShell";

const COHORTS = [
  { key: "founding",       label: "Founding Members", emoji: "🌟", range: "1–250" },
  { key: "early_insiders", label: "Early Insiders",   emoji: "🚀", range: "251–1,000" },
  { key: "pioneers",       label: "Pioneers",         emoji: "🔥", range: "1,001–5,000" },
  { key: "trailblazers",   label: "Trailblazers",     emoji: "💎", range: "5,001–25,000" },
  { key: "drop_nation",    label: "Drop Nation",      emoji: "👑", range: "25,001+" },
];

export default function AdminWaitlist() {
  const [data, setData] = useState({ items: [], total: 0, counts_by_cohort: {} });
  const [filter, setFilter] = useState({ cohort: "", access: "", search: "" });
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter.cohort) params.set("cohort", filter.cohort);
      if (filter.access) params.set("access", filter.access);
      if (filter.search) params.set("search", filter.search);
      params.set("limit", "200");
      const r = await adminApi.get(`/admin/waitlist?${params.toString()}`);
      setData(r.data);
      setSelected(new Set());
    } catch (e) {
      setToast({ kind: "error", msg: e.response?.data?.detail || "Failed to load waitlist" });
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const flash = (kind, msg) => {
    setToast({ kind, msg });
    setTimeout(() => setToast(null), 2600);
  };

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const eligibleIds = data.items.filter((i) => i.kind === "user").map((i) => i.user_id);
  const allSelected = eligibleIds.length > 0 && eligibleIds.every((id) => selected.has(id));
  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(eligibleIds));
  };

  const bulkAccess = async (grant) => {
    const ids = data.items
      .filter((i) => i.kind === "user" && selected.has(i.user_id))
      .map((i) => i.user_id);
    if (!ids.length) return flash("error", "Select at least one registered user");
    setBusy(true);
    try {
      const r = await adminApi.post("/admin/waitlist/access", { user_ids: ids, grant });
      flash("ok", `${grant ? "Granted" : "Revoked"} access · ${r.data.modified} updated`);
      await load();
    } catch (e) {
      flash("error", e.response?.data?.detail || "Bulk update failed");
    } finally { setBusy(false); }
  };

  const cohortAccess = async (cohort_key, grant) => {
    if (!confirm(`${grant ? "Grant" : "Revoke"} access for the entire ${cohort_key.replace("_", " ")} cohort?`)) return;
    setBusy(true);
    try {
      const r = await adminApi.post("/admin/waitlist/access/cohort", { cohort_key, grant });
      flash("ok", `${grant ? "Granted" : "Revoked"} · ${r.data.modified} members updated`);
      await load();
    } catch (e) {
      flash("error", e.response?.data?.detail || "Cohort update failed");
    } finally { setBusy(false); }
  };

  const removeRow = async (row) => {
    if (!confirm(`Delete ${row.email}? This cannot be undone.`)) return;
    setBusy(true);
    try {
      if (row.kind === "user") {
        await adminApi.delete(`/admin/waitlist/user/${row.user_id}`);
      } else {
        await adminApi.delete(`/admin/waitlist/entry/${encodeURIComponent(row.email)}`);
      }
      flash("ok", `Deleted ${row.email}`);
      await load();
    } catch (e) {
      flash("error", e.response?.data?.detail || "Delete failed");
    } finally { setBusy(false); }
  };

  const rowAccess = async (row, grant) => {
    if (row.kind !== "user") return flash("error", "Waitlist entries must sign in first");
    setBusy(true);
    try {
      await adminApi.post("/admin/waitlist/access", { user_ids: [row.user_id], grant });
      flash("ok", `${grant ? "Granted" : "Revoked"} for #${String(row.member_no).padStart(3, "0")}`);
      await load();
    } catch (e) {
      flash("error", e.response?.data?.detail || "Update failed");
    } finally { setBusy(false); }
  };

  const totals = useMemo(() => {
    const c = data.counts_by_cohort || {};
    const total = Object.values(c).reduce((s, v) => s + (v.total || 0), 0);
    const granted = Object.values(c).reduce((s, v) => s + (v.granted || 0), 0);
    return { total, granted, pending: Math.max(0, total - granted) };
  }, [data]);

  const selectedCount = selected.size;

  return (
    <AdminShell>
      <div className="flex items-end justify-between flex-wrap gap-3" data-testid="admin-waitlist-page">
        <div>
          <div className="brand-tag">// Waitlist</div>
          <h1 className="font-display text-5xl uppercase tracking-tight mt-1">Members</h1>
          <p className="text-[#6E6E6E] text-sm mt-1 font-mono-ds">Manage buyer-side access · Promote cohorts to open the site</p>
        </div>
        <div className="flex gap-1">
          <Stat label="Registered" value={totals.total} />
          <Stat label="Access Granted" value={totals.granted} accent />
          <Stat label="Pending" value={totals.pending} />
        </div>
      </div>

      {/* ============== COHORT STRIP ============== */}
      <div className="mt-6 grid gap-2 md:grid-cols-5" data-testid="admin-cohort-strip">
        {COHORTS.map((c) => {
          const counts = data.counts_by_cohort?.[c.key] || { total: 0, granted: 0, pending: 0 };
          return (
            <div key={c.key} className="border border-[#E5E1D8] bg-[#FFFFFF] p-4 flex flex-col gap-2" data-testid={`cohort-card-${c.key}`}>
              <div className="flex items-center justify-between">
                <div className="text-2xl leading-none">{c.emoji}</div>
                <button
                  onClick={() => setFilter((f) => ({ ...f, cohort: f.cohort === c.key ? "" : c.key }))}
                  className={`font-mono-ds text-[9px] uppercase tracking-[0.24em] px-2 py-1 border ${filter.cohort === c.key ? "border-[#FF6A00] bg-[#FF6A00] text-[#161616]" : "border-[#E5E1D8] text-[#6E6E6E] hover:border-[#161616]"}`}
                  data-testid={`cohort-filter-${c.key}`}
                >
                  {filter.cohort === c.key ? "Filtering" : "Filter"}
                </button>
              </div>
              <div className="font-display text-base uppercase tracking-tight leading-tight">{c.label}</div>
              <div className="font-mono-ds text-[10px] uppercase tracking-[0.2em] text-[#6E6E6E]">Members #{c.range}</div>
              <div className="flex items-baseline gap-2">
                <span className="font-display text-3xl tabular-nums text-[#161616]">{counts.granted}</span>
                <span className="text-[#6E6E6E] text-sm font-mono-ds">/ {counts.total}</span>
              </div>
              <div className="font-mono-ds text-[10px] uppercase tracking-[0.2em] text-[#6E6E6E]">
                <span className="text-[#264733] font-bold">{counts.granted}</span> granted · <span className="text-[#FF6A00] font-bold">{counts.pending}</span> pending
              </div>
              <div className="flex gap-1 mt-1">
                <button
                  disabled={busy || counts.pending === 0}
                  onClick={() => cohortAccess(c.key, true)}
                  className="flex-1 h-8 border border-[#264733] bg-[#B9E641] font-mono-ds text-[10px] uppercase tracking-[0.2em] hover:bg-[#264733] hover:text-[#B9E641] disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid={`cohort-promote-${c.key}`}
                >
                  Promote All
                </button>
                <button
                  disabled={busy || counts.granted === 0}
                  onClick={() => cohortAccess(c.key, false)}
                  className="h-8 px-2 border border-[#E5E1D8] bg-[#FFFFFF] font-mono-ds text-[10px] uppercase tracking-[0.2em] text-[#6E6E6E] hover:border-[#FF6A00] hover:text-[#FF6A00] disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid={`cohort-revoke-${c.key}`}
                  title="Revoke all"
                >
                  Revoke
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* ============== TOOLBAR ============== */}
      <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="waitlist-toolbar">
        <input
          value={filter.search}
          onChange={(e) => setFilter((f) => ({ ...f, search: e.target.value }))}
          placeholder="Search email or name"
          className="bg-transparent border border-[#E5E1D8] px-3 h-10 font-mono-ds text-sm focus:border-[#FF6A00] outline-none min-w-[260px]"
          data-testid="waitlist-search"
        />
        <select
          value={filter.access}
          onChange={(e) => setFilter((f) => ({ ...f, access: e.target.value }))}
          className="bg-transparent border border-[#E5E1D8] px-3 h-10 font-mono-ds text-sm focus:border-[#FF6A00] outline-none"
          data-testid="waitlist-access-filter"
        >
          <option value="">All statuses</option>
          <option value="granted">Access granted</option>
          <option value="pending">Pending</option>
        </select>
        {filter.cohort && (
          <button
            onClick={() => setFilter((f) => ({ ...f, cohort: "" }))}
            className="h-10 px-3 border border-[#161616] bg-[#161616] text-[#B9E641] font-mono-ds text-[10px] uppercase tracking-[0.24em]"
            data-testid="waitlist-cohort-clear"
          >
            Clear cohort filter ×
          </button>
        )}
        <div className="ml-auto flex items-center gap-2">
          <span className="font-mono-ds text-[10px] uppercase tracking-[0.24em] text-[#6E6E6E]" data-testid="waitlist-selected-count">
            {selectedCount > 0 ? `${selectedCount} selected` : "No selection"}
          </span>
          <button
            disabled={busy || selectedCount === 0}
            onClick={() => bulkAccess(true)}
            className="h-10 px-3 border border-[#264733] bg-[#B9E641] font-mono-ds text-[11px] uppercase tracking-[0.24em] hover:bg-[#264733] hover:text-[#B9E641] disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="waitlist-bulk-grant"
          >
            Grant Access →
          </button>
          <button
            disabled={busy || selectedCount === 0}
            onClick={() => bulkAccess(false)}
            className="h-10 px-3 border border-[#E5E1D8] bg-[#FFFFFF] font-mono-ds text-[11px] uppercase tracking-[0.24em] text-[#6E6E6E] hover:border-[#FF6A00] hover:text-[#FF6A00] disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="waitlist-bulk-revoke"
          >
            Revoke
          </button>
        </div>
      </div>

      {/* ============== TABLE ============== */}
      <div className="mt-4 border border-[#E5E1D8] bg-[#FFFFFF] overflow-x-auto" data-testid="waitlist-table-wrap">
        <table className="w-full text-sm font-mono-ds min-w-[1100px]">
          <thead className="border-b border-[#E5E1D8] text-[10px] uppercase tracking-[0.3em] text-[#6E6E6E]">
            <tr>
              <th className="text-left p-3 w-10">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleAll}
                  disabled={eligibleIds.length === 0}
                  data-testid="waitlist-select-all"
                  aria-label="Select all"
                />
              </th>
              <th className="text-left p-3 w-16">No.</th>
              <th className="text-left p-3">Member</th>
              <th className="text-left p-3">Cohort</th>
              <th className="text-left p-3">Status</th>
              <th className="text-left p-3">Invited by</th>
              <th className="text-left p-3">Invites</th>
              <th className="text-left p-3">Joined</th>
              <th className="text-left p-3 w-56">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="waitlist-tbody">
            {loading && (
              <tr><td colSpan={9} className="p-8 text-center text-[#6E6E6E]">// Loading…</td></tr>
            )}
            {!loading && data.items.length === 0 && (
              <tr><td colSpan={9} className="p-8 text-center text-[#6E6E6E]" data-testid="waitlist-empty">// No members match these filters</td></tr>
            )}
            {!loading && data.items.map((row) => (
              <MemberRow
                key={`${row.kind}-${row.id}`}
                row={row}
                selected={selected.has(row.user_id)}
                onToggle={() => row.kind === "user" && toggleOne(row.user_id)}
                onGrant={() => rowAccess(row, true)}
                onRevoke={() => rowAccess(row, false)}
                onDelete={() => removeRow(row)}
                busy={busy}
              />
            ))}
          </tbody>
        </table>
      </div>

      {toast && (
        <div
          className={`fixed bottom-6 right-6 border-2 px-4 py-3 font-mono-ds text-xs uppercase tracking-widest shadow-[4px_4px_0_#161616] ${
            toast.kind === "error"
              ? "border-[#FF6A00] bg-[#FFFFFF] text-[#FF6A00]"
              : "border-[#264733] bg-[#B9E641] text-[#161616]"
          }`}
          data-testid="waitlist-toast"
        >
          {toast.msg}
        </div>
      )}
    </AdminShell>
  );
}

function MemberRow({ row, selected, onToggle, onGrant, onRevoke, onDelete, busy }) {
  const isUser = row.kind === "user";
  const padded = String(row.member_no || 0).padStart(3, "0");
  return (
    <tr className="border-b border-[#F0EDE4] hover:bg-[#FFFDF7]" data-testid={`waitlist-row-${row.id}`}>
      <td className="p-3">
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggle}
          disabled={!isUser}
          aria-label={`Select ${row.email}`}
          data-testid={`waitlist-select-${row.id}`}
        />
      </td>
      <td className="p-3 font-display text-lg tabular-nums text-[#264733]">#{padded}</td>
      <td className="p-3">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 border border-[#E5E1D8] overflow-hidden bg-[#FFFFFF] flex items-center justify-center shrink-0">
            {row.picture ? (
              <img src={row.picture} alt="" className="w-full h-full object-cover" />
            ) : (
              <span className="font-display text-sm text-[#FF6A00]">
                {(row.name || row.email || "?").slice(0, 1).toUpperCase()}
              </span>
            )}
          </div>
          <div className="min-w-0">
            <div className="text-[#161616] truncate">{row.name || "—"}</div>
            <div className="text-[10px] text-[#6E6E6E] truncate">{row.email}</div>
          </div>
        </div>
      </td>
      <td className="p-3">
        <span className="inline-flex items-center gap-1.5 border border-[#E5E1D8] bg-[#F8F7F2] px-2 py-1 text-[10px] uppercase tracking-[0.2em] text-[#264733]">
          <span>{row.cohort_emoji}</span>
          <span>{row.cohort_label}</span>
        </span>
      </td>
      <td className="p-3">
        {isUser ? (
          row.access_granted ? (
            <span className="inline-flex items-center gap-1 border border-[#264733] bg-[#B9E641]/40 px-2 py-1 text-[10px] uppercase tracking-[0.2em] text-[#264733] font-bold" data-testid={`row-status-${row.id}`}>
              ● Access
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 border border-[#E5E1D8] bg-[#FFFFFF] px-2 py-1 text-[10px] uppercase tracking-[0.2em] text-[#6E6E6E]" data-testid={`row-status-${row.id}`}>
              ○ Pending
            </span>
          )
        ) : (
          <span className="inline-flex items-center gap-1 border border-[#FF6A00] bg-[#FFFFFF] px-2 py-1 text-[10px] uppercase tracking-[0.2em] text-[#FF6A00]" data-testid={`row-status-${row.id}`}>
            ✎ Email-only
          </span>
        )}
      </td>
      <td className="p-3 text-[#6E6E6E]">
        {row.referred_by_member_no ? (
          <span className="text-[#264733] font-bold">#{String(row.referred_by_member_no).padStart(3, "0")}</span>
        ) : "—"}
      </td>
      <td className="p-3 text-[#6E6E6E] tabular-nums">
        {isUser ? `${row.invites_used}/${row.invites_total}` : "—"}
      </td>
      <td className="p-3 text-[#6E6E6E] text-[10px]">
        {row.joined_at ? new Date(row.joined_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }) : "—"}
      </td>
      <td className="p-3">
        <div className="flex items-center gap-1">
          {isUser ? (
            row.access_granted ? (
              <button
                disabled={busy}
                onClick={onRevoke}
                className="h-7 px-2 border border-[#E5E1D8] bg-[#FFFFFF] text-[#6E6E6E] font-mono-ds text-[10px] uppercase tracking-[0.2em] hover:border-[#FF6A00] hover:text-[#FF6A00] disabled:opacity-40"
                data-testid={`row-revoke-${row.id}`}
              >
                Revoke
              </button>
            ) : (
              <button
                disabled={busy}
                onClick={onGrant}
                className="h-7 px-2 border border-[#264733] bg-[#B9E641] font-mono-ds text-[10px] uppercase tracking-[0.2em] hover:bg-[#264733] hover:text-[#B9E641] disabled:opacity-40"
                data-testid={`row-grant-${row.id}`}
              >
                Grant Access
              </button>
            )
          ) : (
            <span className="text-[10px] text-[#6E6E6E] font-mono-ds uppercase tracking-widest">Awaits sign-in</span>
          )}
          <button
            disabled={busy}
            onClick={onDelete}
            className="h-7 w-7 border border-[#E5E1D8] bg-[#FFFFFF] text-[#6E6E6E] font-mono-ds text-xs hover:border-[#FF6A00] hover:text-[#FF6A00] disabled:opacity-40"
            title="Delete"
            data-testid={`row-delete-${row.id}`}
          >
            ×
          </button>
        </div>
      </td>
    </tr>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className={`border ${accent ? "border-[#FF6A00]" : "border-[#E5E1D8]"} bg-[#FFFFFF] px-4 py-2`}>
      <div className="font-mono-ds text-[9px] uppercase tracking-[0.3em] text-[#6E6E6E]">{label}</div>
      <div className={`font-display text-2xl ${accent ? "text-[#FF6A00]" : "text-[#161616]"}`}>{value}</div>
    </div>
  );
}
